import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, type Team } from '../../services/api';
import { toast } from '../shared/Toast';
import '../shared/ConfirmDialog';

/**
 * Canonical age classes accepted by `PUT /api/admin/teams/{id}/age-group`.
 * Anything outside this set is rejected by the backend with a 400, which
 * is why the UI keeps inputs constrained to these values + "Clear".
 */
const AGE_CLASSES = ['U11', 'U13', 'U15', 'U18', 'U22'] as const;
type AgeClass = (typeof AGE_CLASSES)[number];

@customElement('team-manager')
export class TeamManager extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private teams: Team[] = [];

  @state()
  private loading = true;

  @state()
  private showForm = false;

  @state()
  private editingTeam: Team | null = null;

  @state()
  private formName = '';

  @state()
  private formCity = '';

  @state()
  private saving = false;

  @state()
  private searchQuery = '';

  // Filter: show only teams whose age_group is null/missing. Driven by the
  // banner toggle so Cara can rip through the post-migration audit list.
  @state()
  private showOnlyUnset = false;

  // Delete confirmation
  @state()
  private deleteConfirmOpen = false;

  @state()
  private deletingTeam: Team | null = null;

  @state()
  private deleting = false;

  // Form validation
  @state()
  private formTouched = false;

  // --- Age-class edit modal state ---
  // Modeled on CoachInviteList's edit-email modal: a fixed-overlay card
  // matching the rest of the codebase (vs. native <dialog>), with focus
  // moved onto the select on open for keyboard users.
  @state()
  private ageClassEditing: Team | null = null;

  @state()
  private ageClassValue: AgeClass | '' = '';

  @state()
  private ageClassSaving = false;

  @state()
  private ageClassError = '';

  connectedCallback() {
    super.connectedCallback();
    this.loadTeams();
  }

  private async loadTeams() {
    this.loading = true;
    try {
      this.teams = (await api.getTeams()) || [];
    } catch (error) {
      console.error('Failed to load teams:', error);
      toast.error('Failed to load teams');
    } finally {
      this.loading = false;
    }
  }

  private get filteredTeams(): Team[] {
    let result = this.teams;
    if (this.showOnlyUnset) {
      result = result.filter(t => !t.age_group);
    }
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        t => t.name.toLowerCase().includes(query) ||
             t.city?.toLowerCase().includes(query)
      );
    }
    return result;
  }

  private get unsetCount(): number {
    return this.teams.reduce((n, t) => (t.age_group ? n : n + 1), 0);
  }

  private get formNameError(): string | null {
    if (!this.formTouched) return null;
    if (!this.formName.trim()) return 'Name is required';
    if (this.formName.trim().length < 2) return 'Name must be at least 2 characters';
    return null;
  }

  private openForm(team?: Team) {
    this.editingTeam = team || null;
    this.formName = team?.name || '';
    this.formCity = team?.city || '';
    this.showForm = true;
    this.formTouched = false;
  }

  private closeForm() {
    this.showForm = false;
    this.editingTeam = null;
    this.formName = '';
    this.formCity = '';
    this.formTouched = false;
  }

  private async saveTeam() {
    this.formTouched = true;
    if (this.formNameError) return;

    this.saving = true;
    try {
      if (this.editingTeam) {
        await api.updateTeam(this.editingTeam.id, {
          name: this.formName.trim(),
          city: this.formCity.trim() || undefined,
        });
        toast.success(`Team "${this.formName}" updated`);
      } else {
        await api.createTeam({
          name: this.formName.trim(),
          city: this.formCity.trim() || undefined,
        });
        toast.success(`Team "${this.formName}" created`);
      }
      await this.loadTeams();
      this.closeForm();
      this.dispatchEvent(new CustomEvent('teams-changed', { bubbles: true, composed: true }));
    } catch (error) {
      console.error('Failed to save team:', error);
      toast.error('Failed to save team');
    } finally {
      this.saving = false;
    }
  }

  private confirmDelete(team: Team) {
    this.deletingTeam = team;
    this.deleteConfirmOpen = true;
  }

  private cancelDelete() {
    this.deleteConfirmOpen = false;
    this.deletingTeam = null;
  }

  private async executeDelete() {
    if (!this.deletingTeam) return;

    this.deleting = true;
    try {
      await api.deleteTeam(this.deletingTeam.id);
      toast.success(`Team "${this.deletingTeam.name}" deleted`);
      await this.loadTeams();
      this.cancelDelete();
      this.dispatchEvent(new CustomEvent('teams-changed', { bubbles: true, composed: true }));
    } catch (error) {
      console.error('Failed to delete team:', error);
      toast.error('Failed to delete team');
    } finally {
      this.deleting = false;
    }
  }

  // --- Age class edit modal ---

  private openAgeClassEdit(team: Team) {
    this.ageClassEditing = team;
    this.ageClassValue = (team.age_group as AgeClass | null) || '';
    this.ageClassError = '';
    this.ageClassSaving = false;

    // Move focus onto the select on next paint for keyboard / SR users.
    requestAnimationFrame(() => {
      const select = this.querySelector<HTMLSelectElement>('select.age-class-select');
      select?.focus();
    });
  }

  private cancelAgeClassEdit() {
    if (this.ageClassSaving) return;
    this.ageClassEditing = null;
    this.ageClassValue = '';
    this.ageClassError = '';
  }

  private async saveAgeClass() {
    const team = this.ageClassEditing;
    if (!team || this.ageClassSaving) return;

    // Empty string means "Clear" → send null.
    const next: AgeClass | null = this.ageClassValue ? this.ageClassValue : null;
    const previous: string | null = team.age_group ?? null;
    if (next === previous) {
      this.cancelAgeClassEdit();
      return;
    }

    this.ageClassSaving = true;
    this.ageClassError = '';

    // Optimistic update — patch the row in place. On failure, we restore
    // the previous value below.
    const teamId = team.id;
    this.teams = this.teams.map(t =>
      t.id === teamId ? { ...t, age_group: next } : t
    );

    try {
      const updated = await api.updateTeamAgeGroup(teamId, next);
      // Trust the server response in case it normalizes (or the row was
      // touched elsewhere) — fall back to our optimistic value if the
      // server doesn't echo the field.
      const echoed = updated?.age_group !== undefined ? updated.age_group : next;
      this.teams = this.teams.map(t =>
        t.id === teamId ? { ...t, age_group: echoed } : t
      );
      this.ageClassEditing = null;
      this.ageClassValue = '';
      this.ageClassSaving = false;
      toast.success(
        next
          ? `Age class set to ${next} for "${team.name}"`
          : `Age class cleared for "${team.name}"`
      );
    } catch (err) {
      // Roll back the optimistic update.
      this.teams = this.teams.map(t =>
        t.id === teamId ? { ...t, age_group: previous } : t
      );
      const message = err instanceof Error ? err.message : 'Update failed';
      this.ageClassError = message;
      this.ageClassSaving = false;
      toast.error(message);
    }
  }

  render() {
    return html`
      <div class="space-y-4">
        <div class="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <h3 class="text-lg font-semibold">Teams (${this.teams.length})</h3>
          <div class="flex gap-2">
            <input
              type="text"
              class="input input-bordered input-sm flex-1 min-w-0 sm:w-48"
              placeholder="Search teams..."
              .value=${this.searchQuery}
              @input=${(e: Event) => this.searchQuery = (e.target as HTMLInputElement).value}
            />
            <button class="btn btn-primary btn-sm whitespace-nowrap" @click=${() => this.openForm()}>
              Add Team
            </button>
          </div>
        </div>

        <!-- Audit banner: surfaces post-migration teams with no age class -->
        ${!this.loading && this.unsetCount > 0 ? this.renderAuditBanner() : nothing}

        <!-- Loading skeleton -->
        ${this.loading ? html`
          <div class="space-y-2">
            ${[1, 2, 3].map(() => html`
              <div class="skeleton h-12 w-full"></div>
            `)}
          </div>
        ` : html`
          <!-- Form -->
          ${this.showForm ? this.renderForm() : nothing}

          <!-- Teams list -->
          ${this.renderTeamsList()}

          <!-- Empty state -->
          ${this.filteredTeams.length === 0 ? html`
            <p class="text-center text-base-content/70 py-4">
              ${this.showOnlyUnset
                ? 'No teams without an age class. Audit clear.'
                : this.searchQuery
                  ? 'No teams match your search.'
                  : 'No teams yet. Create one to get started.'}
            </p>
          ` : nothing}
        `}

        <!-- Delete confirmation dialog -->
        <confirm-dialog
          .open=${this.deleteConfirmOpen}
          title="Delete Team"
          .message=${`Delete "${this.deletingTeam?.name}"? This will also remove all players from this team.`}
          confirmText="Delete"
          .destructive=${true}
          .loading=${this.deleting}
          @confirm=${this.executeDelete}
          @cancel=${this.cancelDelete}
        ></confirm-dialog>

        <!-- Age class edit modal -->
        ${this.renderAgeClassModal()}
      </div>
    `;
  }

  /**
   * Audit nudge shown above the teams table whenever any team has no age
   * class set. Includes a one-click "Show only unset" toggle to filter
   * the table down to the audit queue. Disappears the moment unsetCount
   * hits zero.
   */
  private renderAuditBanner() {
    const n = this.unsetCount;
    return html`
      <div
        class="card bg-warning/10 border border-warning/30 animate-fade-in"
        data-testid="age-class-audit-banner"
      >
        <div class="card-body py-3 px-4 flex-row items-center gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-warning shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium">
              <span data-testid="audit-banner-count">${n}</span>
              ${n === 1 ? ' team needs' : ' teams need'} an age class set
            </p>
            <p class="text-xs text-base-content/70">
              Pitch limits fall back to player birthdays for these.
            </p>
          </div>
          <label class="cursor-pointer flex items-center gap-2 whitespace-nowrap">
            <span class="text-xs">Show only unset</span>
            <input
              type="checkbox"
              class="toggle toggle-sm toggle-warning"
              data-testid="show-only-unset-toggle"
              .checked=${this.showOnlyUnset}
              @change=${(e: Event) => this.showOnlyUnset = (e.target as HTMLInputElement).checked}
            />
          </label>
        </div>
      </div>
    `;
  }

  private renderForm() {
    return html`
      <div class="card bg-base-200">
        <div class="card-body">
          <h4 class="card-title">${this.editingTeam ? 'Edit Team' : 'New Team'}</h4>
          <div class="form-control">
            <label class="label"><span class="label-text">Name *</span></label>
            <input
              type="text"
              class="input input-bordered ${this.formNameError ? 'input-error' : ''}"
              placeholder="Enter team name"
              .value=${this.formName}
              @input=${(e: Event) => {
                this.formName = (e.target as HTMLInputElement).value;
                this.formTouched = true;
              }}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') this.saveTeam();
                if (e.key === 'Escape') this.closeForm();
              }}
            />
            ${this.formNameError ? html`
              <label class="label">
                <span class="label-text-alt text-error">${this.formNameError}</span>
              </label>
            ` : nothing}
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">City</span></label>
            <input
              type="text"
              class="input input-bordered"
              placeholder="Enter city (optional)"
              .value=${this.formCity}
              @input=${(e: Event) => this.formCity = (e.target as HTMLInputElement).value}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') this.saveTeam();
                if (e.key === 'Escape') this.closeForm();
              }}
            />
          </div>
          <div class="card-actions justify-end mt-4">
            <button class="btn btn-ghost" @click=${this.closeForm}>Cancel</button>
            <button
              class="btn btn-primary"
              @click=${this.saveTeam}
              ?disabled=${this.saving}
            >
              ${this.saving ? html`<span class="loading loading-spinner loading-sm"></span>` : 'Save'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderTeamsList() {
    if (this.filteredTeams.length === 0) return nothing;

    return html`
      <div class="overflow-x-auto">
        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>City</th>
              <th>Age Class</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            ${this.filteredTeams.map((team) => html`
              <tr class="hover" data-testid="team-row" data-team-id=${team.id}>
                <td class="font-semibold">${team.name}</td>
                <td class="text-base-content/70">${team.city || '-'}</td>
                <td>${this.renderAgeClassBadge(team)}</td>
                <td class="text-right">
                  <button class="btn btn-ghost btn-xs" @click=${() => this.openForm(team)}>Edit</button>
                  <button class="btn btn-ghost btn-xs text-error" @click=${() => this.confirmDelete(team)}>Delete</button>
                </td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  /**
   * Clickable badge in the Age Class column. Sized to a 36px+ tap target
   * (badge has its own height, but the surrounding button enforces the
   * minimum for accessibility). Info color for canonical values, warning
   * for "Not set".
   */
  private renderAgeClassBadge(team: Team) {
    const value = team.age_group;
    const isSet = !!value;
    const label = isSet ? value : 'Not set';
    const badgeClass = isSet ? 'badge-info' : 'badge-warning';
    return html`
      <button
        type="button"
        class="inline-flex items-center min-h-9 px-1 -mx-1 rounded hover:bg-base-200 transition-colors cursor-pointer"
        data-testid="age-class-badge"
        data-value=${value ?? ''}
        aria-label=${isSet
          ? `Age class ${label} for ${team.name}, click to edit`
          : `Age class not set for ${team.name}, click to set`}
        @click=${() => this.openAgeClassEdit(team)}
      >
        <span class="badge ${badgeClass} badge-sm">${label}</span>
      </button>
    `;
  }

  /**
   * Age-class edit modal. Same fixed-overlay card pattern as the
   * edit-email modal in CoachInviteList — backdrop click / Escape /
   * Cancel all close; Save sends an optimistic update and toasts.
   */
  private renderAgeClassModal() {
    const team = this.ageClassEditing;
    if (!team) return nothing;

    const previous: string | null = team.age_group ?? null;
    const current: string | null = this.ageClassValue ? this.ageClassValue : null;
    const dirty = current !== previous;
    const canSave = dirty && !this.ageClassSaving;

    return html`
      <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in"
        @click=${(e: Event) => { if (e.target === e.currentTarget) this.cancelAgeClassEdit(); }}
        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Escape') this.cancelAgeClassEdit(); }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-class-modal-title"
        data-testid="age-class-modal"
      >
        <div class="card bg-base-100 shadow-2xl w-full max-w-sm mx-4 animate-slide-up">
          <div class="card-body">
            <h3 id="age-class-modal-title" class="card-title text-lg">Age class</h3>
            <p class="text-sm text-base-content/70">${team.name}</p>
            <label class="form-control w-full mt-2">
              <span class="label-text text-xs">Canonical age class</span>
              <select
                class="select select-bordered select-sm age-class-select"
                data-testid="age-class-select"
                .value=${this.ageClassValue}
                @change=${(e: Event) => {
                  this.ageClassValue = (e.target as HTMLSelectElement).value as AgeClass | '';
                  if (this.ageClassError) this.ageClassError = '';
                }}
                ?disabled=${this.ageClassSaving}
                aria-invalid=${this.ageClassError ? 'true' : 'false'}
                aria-describedby=${this.ageClassError ? 'age-class-error' : (nothing as unknown as string)}
              >
                <option value="">Clear (fall back to player DOB)</option>
                ${AGE_CLASSES.map(ac => html`
                  <option value=${ac} ?selected=${ac === this.ageClassValue}>${ac}</option>
                `)}
              </select>
            </label>
            ${this.ageClassError
              ? html`<p id="age-class-error" class="text-error text-xs mt-1">${this.ageClassError}</p>`
              : nothing}
            <div class="card-actions justify-end mt-4">
              <button
                class="btn btn-ghost"
                @click=${this.cancelAgeClassEdit}
                ?disabled=${this.ageClassSaving}
              >Cancel</button>
              <button
                class="btn btn-primary"
                data-testid="age-class-save"
                @click=${this.saveAgeClass}
                ?disabled=${!canSave}
              >
                ${this.ageClassSaving
                  ? html`<span class="loading loading-spinner loading-sm"></span>`
                  : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
